import { Data } from './model';

const isNumeric = (x: string) => Number.isInteger(Number(x));

/**
 * It updates an existing array by pushing or creating missing spots in it if
 * a position is bigger than the current length
 *
 * @example
 *  const a = [1];
 *  updateCreateArraySpot(a, 2)
 *  // a [1, undefined, undefined]
 * @param arr
 * @param position
 */
export const updateCreateArraySpot = (arr: (Data | undefined)[], position: number) => {
  if (position === arr.length) {
    arr.push(undefined);
  } else if (position > arr.length) {
    let pivot = arr.length;
    while (pivot <= position) {
      arr.push(undefined);
      pivot++;
    }
  }
  return arr;
};

/**
 * Navigates through data object and returns the targeted path driven by an array
 * of attributes
 * @example
 * // given
 * const data = {
 *  a: { b: 'c' },
 *  arr: [
 *    { x: 'y' }
 *  ]
 * }
 * // when
 * findOrCreateTargetObject(data, ["a", "b"])
 * // then => { b: c }
 *
 * findOrCreateTargetObject(data, ["arr", "0"])
 * // then => {x: 'y'}
 *
 * findOrCreateTargetObject(data, ["arr", "1"])
 * //  {} so it can be updated by external source (deepPatch in this case)
 * // in addition `a` has mutated to:
 * // a {
 * //  a: { b: 'c' },
 * //  arr: [{x: 'y'}, {}]
 * //}
 * @param data {Data}
 * @param path {string}
 */
export const findOrCreateTargetObject = (data: Data, path: string[], leafPath: string) => {
  let isLeafArrayItem = isNumeric(leafPath);
  return path.reduce((acc, attr, index, allItems) => {
    const currentValue = acc[attr];

    let next = allItems[index + 1];
    if (!next && isLeafArrayItem) {
      // if leaf is array item we should create the spot for it
      next = leafPath;
    }
    if (!acc.hasOwnProperty(attr) || (!!next && (currentValue === undefined || !currentValue[next]))) {
      let nextAttr = Number(next);
      if (isNaN(nextAttr)) {
        acc[attr] = {};
      } else {
        acc[attr] = updateCreateArraySpot(currentValue || [], nextAttr);
        if (next !== undefined) {
          acc[attr][nextAttr] = {};
        }
      }
      acc = acc[attr];
    } else {
      acc = currentValue;
    }
    return acc;
  }, data);
};

/**
 * It goes through the object and set's the `value` into the deep path
 * (includes `'.'`) provided in `field` of `data`
 *
 * Note: `numbers` represents arrays
 * @param data Current context or data to update
 * @param field Where to update path
 * @param value Any value, it's the replacement
 *
 * @requires `digger`
 * @returns updated `data` value with the new `value` in the `path`
 */
const deepPatcher = (data: any, paths: string[], value: any): any => {
  const deepPath: string = paths.pop() as string;
  const targetData = findOrCreateTargetObject(data, paths, deepPath);

  if (isNumeric(deepPath)) {
    targetData[deepPath] = Object.assign(targetData[deepPath], value);
  } else {
    Object.assign(targetData, { [deepPath]: value });
  }
  return data;
};

/**
 * It checks if path has a `.` then uses fn, otherwise just sets/replace the value
 * into the `data` using the `field` and the `value`
 *
 * @param data Object to update or add data into
 * @param field Path to update or add data into
 * @param value Value to set
 *
 * @see `deepPatcher`
 * @returns Updated `data` value with the new `value` in the `path`
 *
 * @example
 *  const value = {
 *    a: {
 *       b: 'c',
 *       d: { e: 'f' },
 *     },
 *    x: 'y',
 *    arr: [{ aa: 'bb' }, { aa: 'cc' }],
 * };
 * const res1 = patcher(value, 'a.b', 'cc');
 * res1.a.b === 'cc'; // true
 *
 * const res2 = patcher(value, 'a.d.e', 'g');
 * res2.a.d.e === 'g'; // true
 *
 * const res3 = patcher(value, 'x', 'z');
 * res3.x === 'z'; // true
 *
 * const res4 = patcher(value, 'w', 'z');
 *  res4.w === 'z'; // true, it creates object if not present
 *
 * const res7 = patcher(value, 'arr.0.aa', 'cc');
 * res7.arr[0].aa ==='cc';
 * res7.arr[1].aa ==='cc';
 */
export const patcher = (data: any, field: string, value: any, delimiter = '.'): any => {
  return field.includes(delimiter)
    ? deepPatcher(data, field.split(delimiter), value)
    : Object.assign(data, { [field]: value });
};
